import { CODE_FORMATS, type CodeFormat } from "../code-formats";
import {
  BARCODE_PRODUCT_PATTERNS,
  CODE_PATTERNS_REGEX,
  type CodePattern
} from "../code-patterns";

import { type CodeValidationResult } from "./validate";

/**
 * Honeywell MS7120 Orbit scanners prepend "E" to EAN/UPC product barcodes.
 * This strips the prefix when detected (E + exactly 13, 12, or 8 digits).
 */
const BARCODE_WITH_E_PREFIX = /^E(?<barcode>[0-9]{13}|[0-9]{12}|[0-9]{8})$/;

function stripScannerPrefix(code: string): string {
  return BARCODE_WITH_E_PREFIX.exec(code)?.groups?.barcode ?? code;
}

export function validateFormat(code: string): CodeValidationResult | null {
  const normalizedCode = stripScannerPrefix(code);

  const codeResult = validateCodeGeneric(normalizedCode);
  if (!codeResult) {
    return null;
  }

  let format: CodeFormat | null = null;

  const isBarcodeProduct = isBarcodeProductPattern(codeResult);
  if (isBarcodeProduct) {
    format = CODE_FORMATS.BARCODE_PRODUCT_OR_GENERIC;
  }

  if (!format) {
    format = CODE_FORMATS.GENERIC;
  }

  return {
    format,
    patterns: codeResult.patterns,
    value: normalizedCode
  };
}

type CodeResult = {
  value: string;
  patterns: CodePattern[];
};

function validateCodeGeneric(code: string): CodeResult | null {
  // Find the matching patterns and its corresponding format name
  const formatEntries = Object.entries(CODE_PATTERNS_REGEX).filter(
    ([_, pattern]) => pattern.test(code)
  );

  if (formatEntries.length === 0) return null;

  const result: CodeResult = {
    value: code,
    patterns: []
  };

  for (const [formatName, _] of formatEntries) {
    result.patterns.push(formatName as CodePattern);
  }

  return result;
}

function isBarcodeProductPattern(result: CodeResult): boolean {
  return result.patterns.some((format) => format in BARCODE_PRODUCT_PATTERNS);
}
